import { dasModal } from "../modal_v2.js";
import { identityService } from "./identityService_v2.js";
import { profile } from "./profile_v2.js";
import { eventHub } from "../eventHub_v2.js";
import { loadingTextHTML, fillLoadedText } from "./accountDetailsUI_v2.js";

const render = async () => {
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return { status: pAIdentityResponse.status };
  }
  const pAIdentity = pAIdentityResponse.identity;
  // The identity token doesn't carry the email. The pop-up opens right away
  // with a placeholder, then fills it in from getMyAccount.
  const html = `
    <div class="popUpHead">Verify Email</div>
    <div class="center">
      <span id="verifyEmailStatusText"></span>${loadingTextHTML("verifyEmailAddress")}
    </div>
    <br>
    <div id="verifyform">
      <div class="center">
        <label for="verifyInput">Code:</label>
        <input 
          class="bottom7" 
          type="text" 
          id="verifyInput" 
          placeholder="Code that was emailed"
          autocomplete="off"
          >
        <div class="inputMessages center" id="verifyMessage"></div>
      </div>
      <div class="center">
        <button 
          id="verifySubmit" 
          class="submitButton bottom7 button workADayButtons">
          Submit
        </button>
        <div class="inputMessages center" id="verifySubmitMessage"></div>
      </div>
      <div class="center">
        <button 
          id="sendNewCode" 
          class="submitButton bottom7 button workADayButtons">
          Send New Code
        </button>
        <div class="inputMessages center" id="sendNewCodeMessage"></div>
      </div>
    </div>
  `;
  profile.render("small");
  dasModal.render(html);

  // Now fill in the email (textContent: it's user-entered text)
  const accountResult = await identityService.getMyAccount();
  if (accountResult.status != 200) {
    fillLoadedText(
      "verifyEmailAddress",
      accountResult.message || "Unable to load",
    );
    return { status: accountResult.status };
  }
  const email = accountResult.account.email;
  const verified = accountResult.account.emailVerified;
  const statusText = document.getElementById("verifyEmailStatusText");
  if (statusText) {
    statusText.textContent = verified
      ? "The email has been verified: "
      : "A verification code has been sent to: ";
  }
  if (!fillLoadedText("verifyEmailAddress", email)) {
    return { status: 200 }; // closed while loading, so don't send a code
  }
  if (!verified) {
    // Send a code automatically, but only if there isn't one that still
    // works: otherwise the code already in their inbox would stop working.
    await identityService.sendEmailCode({ onlyIfNone: true });
  }

  const sendNewCodeMessage = document.getElementById("sendNewCodeMessage");
  if (!sendNewCodeMessage) {
    return { status: 200 }; // closed meanwhile
  }
  if (pAIdentity.emailPrefs?.account === false) {
    document.getElementById("sendNewCode").disabled = true;
    sendNewCodeMessage.textContent =
      "Your email preferences are set to 'Off'. To email this code, go to preferences (gear icon), email notifications, profile, and set it to 'On'.";
    return;
  }
  if (verified === true) {
    sendNewCodeMessage.textContent =
      "This email has been verified, but you can still send a new code and re-verify it.";
  }
};

const handleSendCode = async (event) => {
  event.stopPropagation();
  event.preventDefault();
  const sendNewCodeButton = document.getElementById("sendNewCode");
  const sendNewCodeMessage = document.getElementById("sendNewCodeMessage");

  sendNewCodeButton.disabled = true;
  document.body.style.cursor = "wait";
  await new Promise(requestAnimationFrame);
  const result = await identityService.sendEmailCode();
  document.body.style.cursor = "default";
  sendNewCodeButton.disabled = false;
  if (result.status != 200) {
    sendNewCodeMessage.textContent =
      result.message || "Error: Unable to send the code";
    return;
  }
  sendNewCodeMessage.textContent = "A new code was emailed.";
};

const isCodeValid = (code) => {
  if (typeof code != "string") {
    return { status: 400, message: "Please enter six numbers" };
  }
  if (/^[0-9]{6}$/.test(code) === false) {
    return { status: 400, message: "Please enter six numbers" };
  }
  return { status: 200, message: "" };
};

const handleVerifyInputChange = () => {
  const verifyMessage = document.getElementById("verifyMessage");
  const codeEntered = document.getElementById("verifyInput").value;
  const resultCodeValid = isCodeValid(codeEntered);
  if (resultCodeValid.status != 200) {
    verifyMessage.textContent = resultCodeValid.message;
    return;
  }
  if (resultCodeValid.status === 200) {
    verifyMessage.textContent = "";
    return;
  }
};

const handleVerifySubmit = async () => {
  const verifySubmitButton = document.getElementById("verifySubmit");
  const verifySubmitMessage = document.getElementById("verifySubmitMessage");
  const codeEntered = document.getElementById("verifyInput").value;
  const resultCodeValid = isCodeValid(codeEntered);
  if (resultCodeValid.status != 200) {
    verifySubmitMessage.textContent = resultCodeValid.message;
    return;
  }
  verifySubmitButton.disabled = true;
  document.body.style.cursor = "wait";
  verifySubmitButton.style.cursor = "wait";
  verifySubmitMessage.textContent = "Submitting...";
  const resultVerify = await identityService.verifyCode(codeEntered);
  if (resultVerify.status != 200) {
    verifySubmitButton.disabled = false;
    document.body.style.cursor = "default";
    verifySubmitButton.style.cursor = "default";
    verifySubmitMessage.textContent =
      resultVerify.message || "The code was incorrect";
    return;
  }

  document.body.style.cursor = "default";
  verifySubmitButton.style.cursor = "default";
  verifySubmitMessage.textContent = "";
  dasModal.close();
  await new Promise(requestAnimationFrame);
  dasModal.render(`<div class="max">The email has been verified</div>`);
};

// Enter in the code box does the same as clicking Submit (skipped while a
// submit is already in flight, or while an IME is composing).
const handleVerifyInputKeydown = (event) => {
  if (event.key !== "Enter") return;
  if (event.isComposing || event.keyCode === 229) return;
  event.preventDefault();
  const verifySubmitButton = document.getElementById("verifySubmit");
  if (!verifySubmitButton || verifySubmitButton.disabled) return;
  handleVerifySubmit();
};

eventHub.registerEvent("click", "sendNewCode", handleSendCode);
eventHub.registerEvent("click", "verifySubmit", handleVerifySubmit);
// The "six numbers" hint updates as you type. (It was wired to "click",
// so it only updated when the box was clicked and never cleared.)
eventHub.registerEvent("keyup", "verifyInput", handleVerifyInputChange);
eventHub.registerEvent("keydown", "verifyInput", handleVerifyInputKeydown);

export const verifyEmailPop = {
  render,
};
